<script setup lang="ts" generic="Value">
import {
  EnumerationOptionDto,
  EnumerationValueDto,
  type EnumerationValueDtoValue,
} from '@/api/generator';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import { EnumerationValueProps } from '@/components/forms/types.ts';
import { I18nKey } from '@/utils/typings.ts';
import { useTranslation } from 'i18next-vue';
import { computed, ref, watch } from 'vue';

const props = defineProps<EnumerationValueProps<Value>>();

const enumerationValue = defineModel<EnumerationValueDto>('enumerationValue', {
  required: true,
});

const { t } = useTranslation();

const emits = defineEmits<{
  blur: [event: FocusEvent];
}>();

const value = ref<EnumerationValueDtoValue | null | undefined>(enumerationValue.value.value);

// Keep the displayed selection in sync when the model is replaced from outside (e.g. a parent
// reverting or swapping the whole DTO), not only when the user picks an option here.
watch(
  () => enumerationValue.value?.value,
  (modelValue) => {
    value.value = modelValue;
  },
);

const selectOptions = computed(() => {
  return enumerationValue.value?.options?.map((option) => {
    return {
      title: `${t(option.name as I18nKey)}`,
      value: option,
    };
  });
});

function onUpdate(input: EnumerationOptionDto | null | undefined) {
  // set to 'undefined' in case of 'null', to make the 'required' field validation working
  value.value = input || undefined;
  enumerationValue.value = {
    ...enumerationValue.value,
    value: input || undefined,
  };
}
</script>

<template>
  <Autocomplete
    v-bind="props"
    :items="props.items ? props.items : selectOptions"
    :modelValue="value"
    :multiple="false"
    @update:modelValue="onUpdate"
    @blur="emits('blur', $event)"
  />
</template>
